import React, { useEffect, useRef, useState } from 'react';
import { Button } from './ui/button';
import { Calendar, Phone, ArrowRight, Heart, Shield, Award } from 'lucide-react';
import { InsurancePopup } from './InsurancePopup';
import { NabihCertificatePopup } from './NabihCertificatePopup';

export const Hero = () => {
  const heroRef = useRef(null);
  const [showInsurancePopup, setShowInsurancePopup] = useState(false);
  const [showCertificatePopup, setShowCertificatePopup] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('fade-in');
          }
        });
      },
      { threshold: 0.1 }
    );

    if (heroRef.current) {
      observer.observe(heroRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const scrollToContact = () => {
    const element = document.querySelector('#contact');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const features = [
    { icon: Heart, label: '24/7 Emergency Care' },
    { icon: Shield, label: 'Expert Medical Team' },
    { icon: Award, label: 'Advanced Technology' },
  ];

  return (
    <>
      <section
        id="home"
        ref={heroRef}
        className="relative min-h-screen flex items-center pt-20 overflow-hidden"
        style={{
          background: 'linear-gradient(135deg, hsl(207 89% 97%) 0%, hsl(195 77% 95%) 50%, hsl(207 89% 97%) 100%)'
        }}
      >
        {/* Decorative Elements */}
        <div className="absolute top-20 right-10 w-72 h-72 bg-[hsl(var(--primary))]/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 left-10 w-96 h-96 bg-[hsl(var(--secondary))]/10 rounded-full blur-3xl"></div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left Content */}
            <div className="space-y-8">
              {/* Trust Badge and New Elements - Stacked Vertically */}
              <div className="flex flex-col gap-3 max-w-md">
                <div className="inline-flex items-center space-x-2 bg-[hsl(var(--card))] px-4 py-2 rounded-full shadow-[var(--shadow-sm)] w-fit">
                  <div className="w-2 h-2 bg-[hsl(var(--success))] rounded-full animate-pulse"></div>
                  <span className="text-sm font-medium text-[hsl(var(--foreground))]">
                    Trusted Healthcare Since 2017
                  </span>
                </div>
                
                {/* NABH Certificate Element */}
                <button 
                  onClick={() => setShowCertificatePopup(true)}
                  className="inline-flex items-center space-x-2 bg-[hsl(var(--primary))] px-4 py-2 rounded-full shadow-[var(--shadow-sm)] hover:shadow-[var(--shadow-md)] transition-all duration-300 cursor-pointer group w-fit"
                >
                  <Award className="w-4 h-4 text-white" />
                  <span className="text-sm font-medium text-white">
                    NABH-Certified Quality Standards
                  </span>
                </button>

                {/* Insurance Accepted Element */}
                <button 
                  onClick={() => setShowInsurancePopup(true)}
                  className="inline-flex items-center space-x-2 bg-[hsl(var(--accent))] px-4 py-2 rounded-full shadow-[var(--shadow-sm)] hover:shadow-[var(--shadow-md)] transition-all duration-300 cursor-pointer group w-fit"
                >
                  <Shield className="w-4 h-4 text-white" />
                  <span className="text-sm font-medium text-white">
                    All Insurance Accepted
                  </span>
                </button>
              </div>

              <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold text-[hsl(var(--foreground))] leading-tight">
                Your Health is Our
                <span className="block mt-2 text-gradient">Top Priority</span>
              </h1>

              <p className="text-lg sm:text-xl text-[hsl(var(--muted-foreground))] leading-relaxed max-w-2xl">
                Experience world-class healthcare with our team of expert doctors,
                state-of-the-art facilities, and compassionate care. We're here for
                you 24/7.
              </p>

              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-4">
                <Button
                  variant="premium"
                  size="lg"
                  onClick={scrollToContact}
                  className="group font-semibold"
                >
                  <Calendar className="w-5 h-5 mr-2" />
                  Book Appointment
                  <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                </Button>
                <Button
                  variant="outline"
                  size="lg"
                  className="group font-semibold"
                  onClick={() => window.open('tel:02067527445')}
                >
                  <Phone className="w-5 h-5 mr-2" />
                  Emergency: 02067527445
                </Button>
              </div>

              {/* Feature Pills */}
              <div className="flex flex-wrap gap-4 pt-4">
                {features.map((feature, index) => (
                  <div
                    key={index}
                    className="flex items-center space-x-2 bg-[hsl(var(--card))] px-4 py-3 rounded-xl shadow-[var(--shadow-sm)] hover:shadow-[var(--shadow-md)] transition-all duration-300 hover:scale-105"
                  >
                    <feature.icon className="w-5 h-5 text-[hsl(var(--primary))]" />
                    <span className="text-sm font-medium text-[hsl(var(--foreground))]">
                      {feature.label}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Right Content - Hero Image with 3D Effect */}
            <div className="relative">
              <div className="relative group">
                {/* Main Image */}
                <div className="relative rounded-3xl overflow-hidden shadow-[var(--shadow-xl)] transform transition-all duration-500 group-hover:scale-105 group-hover:rotate-2">
                  <img
                    src="https://images.unsplash.com/photo-1719934398679-d764c1410770?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODB8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBob3NwaXRhbHxlbnwwfHx8fDE3NjExODU3MTl8MA&ixlib=rb-4.1.0&q=85"
                    alt="Modern Hospital Facility"
                    className="w-full h-[600px] object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[hsl(var(--primary))]/20 to-transparent"></div>
                </div>

                {/* Floating Stats Cards */}
                <div className="absolute -bottom-6 -left-6 bg-[hsl(var(--card))] p-6 rounded-2xl shadow-[var(--shadow-xl)] backdrop-blur-sm border border-[hsl(var(--border))] float-animation">
                  <div className="flex items-center space-x-4">
                    <div className="w-14 h-14 rounded-full bg-gradient-to-br from-[hsl(var(--primary))] to-[hsl(var(--secondary))] flex items-center justify-center">
                      <Heart className="w-7 h-7 text-white" />
                    </div>
                    <div>
                      <p className="text-3xl font-bold text-[hsl(var(--foreground))]">50K+</p>
                      <p className="text-sm text-[hsl(var(--muted-foreground))]">Happy Patients</p>
                    </div>
                  </div>
                </div>

                <div className="absolute -top-6 -right-6 bg-[hsl(var(--card))] p-6 rounded-2xl shadow-[var(--shadow-xl)] backdrop-blur-sm border border-[hsl(var(--border))]" style={{ animationDelay: '1s' }}>
                  <div className="flex items-center space-x-4">
                    <div className="w-14 h-14 rounded-full bg-gradient-to-br from-[hsl(var(--accent))] to-[hsl(var(--secondary))] flex items-center justify-center">
                      <Award className="w-7 h-7 text-white" />
                    </div>
                    <div>
                      <p className="text-3xl font-bold text-[hsl(var(--foreground))]">20+</p>
                      <p className="text-sm text-[hsl(var(--muted-foreground))]">Expert Doctors</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Popup Modals */}
      {showCertificatePopup && (
        <NabihCertificatePopup onClose={() => setShowCertificatePopup(false)} />
      )}
      
      {showInsurancePopup && (
        <InsurancePopup onClose={() => setShowInsurancePopup(false)} />
      )}
    </>
  );
};